import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Children, forwardRef, isValidElement, useState } from "react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { TableRow } from "../table-row/TableRow";
import { TableCell } from "../table-cell/TableCell";
import { ExpandableCell } from "../expandable-cell/ExpandableCell";
import { NestLevelContext, useNestLevel } from "../nest-level-context";
import { stringMapDefault } from "./ExpandableRow.stringMap";
const isRowDefinition = (row) => Boolean(row.cells);
const componentName = "ExpandableRow";
export const ExpandableRow = forwardRef((props, ref) => {
    const { children, indexCellComponent, isInitialExpanded = false, nestedRows, onToggle, stringMap: stringMapProp, ...rest } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const [isExpandedUncontrolled, setIsExpandedUncontrolled] = useState(isInitialExpanded || false);
    const isExpandedStateControlled = props.isExpanded !== null && props.isExpanded !== undefined;
    const isExpanded = isExpandedStateControlled ? props.isExpanded : isExpandedUncontrolled;
    const nestLevel = useNestLevel();
    const handleToggle = () => {
        if (onToggle) {
            onToggle({ isExpanded: !isExpanded });
        }
        if (!isExpandedStateControlled) {
            setIsExpandedUncontrolled(!isExpanded);
        }
    };
    // @ts-expect-error This works to support Fragments https://stackoverflow.com/questions/49012718/check-if-a-child-is-react-fragment
    const rowCells = Children.toArray(children.type === React.Fragment ? children.props.children : children);
    if (rowCells.length === 0 || !isValidElement(rowCells[0])) {
        throw new Error("ExpandableRow must have at least one child.");
    }
    const IndexCellComponent = indexCellComponent ? indexCellComponent : ExpandableCell;
    rowCells[0] = (_jsx(IndexCellComponent, { ...rowCells[0].props, ...(indexCellComponent ? {} : { expandText: stringMap.expand, collapseText: stringMap.collapse }), onToggle: nestedRows ? handleToggle : null, isExpanded: isExpanded, nestLevel: nestLevel }, rowCells[0].props.id || "rowToggle-0"));
    const parentRow = (_jsx(TableRow, { ref: ref, ...rest, children: rowCells }));
    if (nestedRows && isExpanded) {
        // Support fragments by checking if nestedRows is an array. If not, it's a fragment. Return it directly.
        const nestedRowElements = Array.isArray(nestedRows)
            ? nestedRows.map((row, rowIndex) => {
                if (isRowDefinition(row)) {
                    const RowComponent = row.component || ExpandableRow;
                    const key = `nested-row-${nestLevel}-${row.id || rowIndex}`;
                    return (_jsx(RowComponent, { id: "nested-row-" + key, ...row, children: row.cells.map(({ children, ...cell }, cellIndex) => {
                            const CellComponent = cell.component ? cell.component : TableCell;
                            return (_jsx(CellComponent, { ...cell, children: children }, cell.id ?? cellIndex));
                        }) }, "nested-row-" + key));
                }
                return row;
            })
            : nestedRows;
        return (_jsxs(_Fragment, { children: [parentRow, _jsx(NestLevelContext.Provider, { value: nestLevel + 1, children: nestedRowElements })] }));
    }
    return parentRow;
});
ExpandableRow.displayName = componentName;
